import React, { useState, useRef, useEffect } from 'react';
import './App.css';

const App = () => {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);
  
  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Initial greeting
  useEffect(() => {
    setMessages([{
      type: 'assistant',
      content: "👋 Hi! I'm Finn, your AI shopping assistant powered by real-time Amazon data. I specialize in finding both popular products AND those lesser-known items with great quality and better value.\n\nWhat are you looking for today? I'll find you the best options with affiliate links ready for purchase!",
      timestamp: new Date(),
    }]);
  }, []);

  const sendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = {
      type: 'user',
      content: inputValue.trim(),
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      const response = await fetch(`${backendUrl}/api/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: inputValue.trim(),
          session_id: sessionId
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (!sessionId) {
        setSessionId(data.session_id);
      }

      // Debug logging
      console.log('Received AI response:', data.response?.substring(0, 200));
      console.log('Received products:', data.products);

      const assistantMessage = {
        type: 'assistant',
        content: data.response,
        products: data.products || [],
        timestamp: new Date(),
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage = {
        type: 'assistant',
        content: '❌ Sorry, I encountered an error. Please try again.',
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const formatPrice = (price, originalPrice, hasRealDiscount) => {
    const discount = (hasRealDiscount && originalPrice > price) ? Math.round(((originalPrice - price) / originalPrice) * 100) : 0;
    return (
      <div className="flex items-center gap-2">
        <span className="text-2xl font-bold text-green-600">${price}</span>
        {discount > 0 && (
          <>
            <span className="text-lg text-gray-500 line-through">${originalPrice}</span>
            <span className="bg-red-500 text-white px-2 py-1 rounded text-sm font-medium">
              {discount}% OFF
            </span>
          </>
        )}
      </div>
    );
  };

  const ProductCard = ({ product, showBadge = false, productIndex, explanation }) => (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow p-4 mb-4 relative">
      {/* Hidden Gem or Popular Badge - only show if showBadge is true */}
      {showBadge && (
        <div className="absolute top-2 right-2 z-10">
          {product.is_hidden_gem ? (
            <span className="bg-purple-100 text-purple-800 text-xs font-medium px-2 py-1 rounded-full">
              🔹 Hidden Gem
            </span>
          ) : (
            <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-1 rounded-full">
              ⭐ Popular
            </span>
          )}
        </div>
      )}
      
      <div className="flex gap-4">
        <img 
          src={product.image} 
          alt={product.title}
          className="w-20 h-20 object-cover rounded-lg flex-shrink-0"
          onError={(e) => {
            e.target.src = "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80";
          }}
        />
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-gray-900 text-sm mb-2 line-clamp-2 pr-20">
            {product.title}
          </h3>
          
          {formatPrice(product.price, product.original_price, product.has_real_discount)}
          
          <div className="flex items-center gap-2 mt-2">
            {product.rating && (
              <>
                <div className="flex items-center">
                  <span className="text-yellow-400">★</span>
                  <span className="ml-1 text-sm text-gray-600">{product.rating.toFixed(1)}</span>
                </div>
                {product.review_count && (
                  <span className="text-sm text-gray-500">
                    ({product.review_count.toLocaleString()} reviews)
                  </span>
                )}
              </>
            )}
            {!product.rating && !product.review_count && (
              <span className="text-sm text-gray-500">No reviews available</span>
            )}
          </div>
          
          <div className="flex flex-wrap gap-1 mt-2">
            {(product.features || []).slice(0, 2).map((feature, idx) => (
              <span key={idx} className="bg-blue-50 text-blue-700 px-2 py-1 rounded text-xs">
                {feature}
              </span>
            ))}
          </div>
          
          {/* Affiliate Link Button */}
          <div className="mt-3">
            <button
              onClick={() => {
                console.log('=== BUTTON CLICK DEBUG ===');
                console.log('Product:', product);
                console.log('Product title:', product.title);
                console.log('Affiliate URL:', product.affiliate_url);
                console.log('Amazon URL:', product.amazon_url);
                
                // Use affiliate_url first, then fallback to amazon_url
                const targetUrl = product.affiliate_url || product.amazon_url;
                console.log('Target URL:', targetUrl);
                
                if (targetUrl) {
                  console.log('Opening URL:', targetUrl);
                  window.open(targetUrl, '_blank', 'noopener,noreferrer');
                } else {
                  console.error('No URL available for product');
                  alert('Product link is not available. Please try again later.');
                }
              }}
              className="inline-flex items-center px-3 py-2 bg-orange-500 text-white text-sm font-medium rounded-lg hover:bg-orange-600 transition-colors cursor-pointer"
              style={{ cursor: 'pointer' }}
            >
              <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z"/>
                <path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z"/>
              </svg>
              View on Amazon
            </button>
          </div>
        </div>
      </div>
      
      {/* Individual "More about this product" explanation */}
      <div className="mt-4 pt-3 border-t border-gray-100">
        <h4 className="font-semibold text-gray-800 mb-2">More about this product</h4>
        <p className="text-gray-600 text-sm leading-relaxed">
          {explanation || `This product excels with a ${product.rating?.toFixed(1) || 'high'}/5 rating from ${(product.review_count || 0).toLocaleString()} customers. It offers excellent quality and value for your needs.`}
        </p>
      </div>
    </div>
  );

  const MessageBubble = ({ message }) => (
    <div className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'} mb-4`}>
      <div className={`max-w-3xl ${message.type === 'user' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-900'} rounded-lg px-4 py-3`}>
        {message.type === 'assistant' && (
          <div className="flex items-center mb-2">
            <div className="w-6 h-6 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full flex items-center justify-center mr-2">
              <span className="text-white text-xs font-bold">AI</span>
            </div>
            <span className="text-sm font-medium text-gray-700">Finn</span>
          </div>
        )}
        
        <div className="whitespace-pre-wrap">{message.content}</div>
        
        {message.products && message.products.length > 0 && (
          <div className="mt-4">
            <div className="space-y-4">
              {message.products.map((product, index) => (
                <ProductCard 
                  key={product.id} 
                  product={product} 
                  showBadge={false} 
                  productIndex={index + 1}
                  explanation={product.explanation || `This product excels with a ${product.rating?.toFixed(1) || 'high'}/5 rating from ${(product.review_count || 0).toLocaleString()} customers. It offers excellent quality and value for your needs.`}
                />
              ))}
            </div>
          </div>
        )}
        
        <div className="text-xs opacity-70 mt-2">
          {message.timestamp.toLocaleTimeString()}
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white">
        <div className="max-w-6xl mx-auto px-4 py-16">
          <div className="text-center">
            <h1 className="text-5xl font-bold mb-6">
              Finn AI
              <br />
              <span className="text-3xl font-medium">Your personal Amazon shopping assistant.</span>
            </h1>
            <p className="text-xl leading-relaxed max-w-4xl mx-auto">
              Ask Finn things like "show me the top three cordless vacuums with at least 4.4 stars and 1,000+ reviews"
            </p>
            <div className="mt-8">
              <div className="inline-flex items-center bg-white bg-opacity-20 rounded-full px-4 py-2">
                <span className="bg-green-400 w-3 h-3 rounded-full mr-2"></span>
                <span className="text-sm font-medium">Beta Version - Free to Use</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* AI Shopping Assistant Section */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-lg h-[calc(100vh-200px)] flex flex-col">
          
          {/* Messages Area */}
          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            {messages.map((message, index) => (
              <MessageBubble key={index} message={message} />
            ))}
            
            {isLoading && (
              <div className="flex justify-start mb-4">
                <div className="bg-gray-100 rounded-lg px-4 py-3 max-w-xs">
                  <div className="flex items-center">
                    <div className="w-6 h-6 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full flex items-center justify-center mr-2">
                      <span className="text-white text-xs font-bold">AI</span>
                    </div>
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div className="border-t bg-gray-50 p-4">
            <div className="flex gap-2">
              <textarea
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask me to find products on Amazon... (e.g., 'I need a good bluetooth speaker under $100' or 'Find me kitchen gadgets with great value')"
                className="flex-1 resize-none border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                rows="2"
                disabled={isLoading}
              />
              <button
                onClick={sendMessage}
                disabled={isLoading || !inputValue.trim()}
                className="bg-blue-500 hover:bg-blue-600 disabled:bg-gray-300 text-white px-6 py-3 rounded-lg font-medium transition-colors flex-shrink-0 self-end"
              >
                {isLoading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  'Send'
                )}
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              💡 Try: "Best wireless earbuds", "Hidden gem kitchen gadgets", "Gaming laptop under $1000 with great value"
            </p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="max-w-4xl mx-auto px-4 py-6 text-center">
        <p className="text-sm text-gray-600 italic mb-2">
          Disclosure: As an Amazon Associate, we earn from qualifying purchases
        </p>
        <p className="text-sm text-gray-500">
          © FinnAI All Rights Reserved
        </p>
      </footer>
    </div>
  );
};

export default App;